import { toast } from "sonner";
import { useEditState } from "../expenses/expenseStates";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { useDeleteDialog } from "../utils/isDialogOpen";
import { toTitleCase } from "../utils/titleCase";
import { useSelectedState } from "./buildingStates";
import { deleteBuildings } from "./deleteBuilding";
import { editBuilding } from "./editBuilding";
import { fetchBuildings } from "./getBuilding";
import { Input } from "../ui/input";
import { Controller } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const BuildingTable = ({}) => {
  const { buildings } = fetchBuildings();
  const {
    handleSubmit,
    register,
    updateBuildingFn,
    errors,
    isSubmitting,
    control,
  } = editBuilding();
  const { deleteBuildingFn } = deleteBuildings();
  const { isEditOpen, setIsEditOpen } = useEditState();
  const { isDeleteDialogOpen, setIsDeleteDialogOpen } = useDeleteDialog();
  const { selectedBuilding, setSelectedBuilding } = useSelectedState();

  const states = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
  ];

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-Hemmers text-lg">Customer</TableHead>
          <TableHead className="font-Hemmers text-lg">Letter Id</TableHead>
          <TableHead className="font-Hemmers text-lg">Address</TableHead>
          <TableHead className="font-Hemmers text-lg">More</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {buildings?.buildings.map((buildings) => (
          <TableRow key={buildings.id}>
            <TableCell className="font-Hemmers text-lg text-black">
              {toTitleCase(buildings.buildingName)}
            </TableCell>
            <TableCell className="font-Hemmers text-lg text-black">
              {buildings.letterIdBuilding
                ? toTitleCase(buildings.letterIdBuilding)
                : ""}
            </TableCell>
            <TableCell className="font-Hemmers text-lg text-black">
              {toTitleCase(buildings.address) +
                ", " +
                toTitleCase(buildings.buildingCity) +
                ", " +
                buildings.buildingState}
            </TableCell>

            <TableCell>
              <Popover>
                <PopoverTrigger asChild>
                  <Button className="bg-transparent font-Hemmers text-lg text-black hover:bg-gray-200">
                    ...
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="pointer-events-auto w-24 p-0">
                  <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => setSelectedBuilding(buildings)}
                        className="w-full rounded-b-none rounded-t-md bg-[#FAFAFA] text-black hover:bg-gray-200"
                      >
                        Edit
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="border-transparent bg-inherit">
                      <DialogHeader>
                        <DialogTitle className="text-black">
                          Edit Customer
                        </DialogTitle>
                        <DialogDescription>
                          Fill in to edit a customer
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        onSubmit={handleSubmit(async (data) => {
                          if (selectedBuilding) {
                            try {
                              await updateBuildingFn({
                                buildingId: buildings.id,
                                buildingName: data.buildingName,
                                letterIdBuilding: data.letterIdBuilding,
                                address: data.address,
                                buildingCity: data.buildingCity,
                                buildingState: data.buildingState,
                                buildingZip: data.buildingZip,
                                buildingCountry: data.buildingCountry,
                              });
                              setIsEditOpen(false);
                            } catch (err) {
                              console.error(err);
                              toast.error("Error updating building");
                            }
                          }
                        })}
                      >
                        <div className="flex space-x-4">
                          {/* Container for building name input */}
                          <div className="flex w-96 flex-col">
                            <label
                              htmlFor="buildingName"
                              className="block text-sm font-medium text-black"
                            >
                              Building Name
                            </label>
                            <Input
                              {...register("buildingName", {
                                required: "Building name is required",
                              })}
                              type="text"
                              id="buildingName"
                              autoComplete="off"
                              className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                            />
                            {errors.buildingName && (
                              <span className="text-sm text-rose-500">
                                {errors.buildingName.message}
                              </span>
                            )}
                          </div>

                          {/* Container for letter ID input */}
                          <div className="flex w-16 flex-col">
                            <label
                              htmlFor="letterIdBuilding"
                              className="block text-sm font-medium text-black"
                            >
                              Letter Id
                            </label>
                            <Input
                              {...register("letterIdBuilding", {
                                required: "Building letter id is required",
                              })}
                              type="text"
                              id="letterIdBuilding"
                              autoComplete="off"
                              className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                            />
                            {errors.letterIdBuilding && (
                              <span className="text-sm text-rose-500">
                                {errors.letterIdBuilding.message}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Container for address input */}
                        <div className="mt-4">
                          <label
                            htmlFor="address"
                            className="block text-sm font-medium text-black"
                          >
                            Address
                          </label>
                          <Input
                            {...register("address", {
                              required: "Building address is required",
                            })}
                            type="text"
                            id="address"
                            autoComplete="off"
                            className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                          />
                          {errors.address && (
                            <span className="text-sm text-rose-500">
                              {errors.address.message}
                            </span>
                          )}
                        </div>
                        <div className="mt-4 flex space-x-4">
                          <div>
                            <label
                              htmlFor="state"
                              className="block text-sm font-medium text-black"
                            >
                              State
                            </label>
                            <Controller
                              name="buildingState"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  onValueChange={field.onChange}
                                  value={field.value}
                                >
                                  <SelectTrigger
                                    className={`mt-1 w-[80px] rounded-md border-zinc-300 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm ${
                                      field.value
                                        ? "text-zinc-900"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="w-[80px]">
                                    {states.map((state) => (
                                      <SelectItem
                                        key={state.value}
                                        value={state.label}
                                      >
                                        {state.value}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="city"
                              className="block text-sm font-medium text-black"
                            >
                              City
                            </label>
                            <Input
                              type="text"
                              id="buildingCity"
                              autoComplete="off"
                              className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                              {...register("buildingCity")}
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="zip"
                              className="block text-sm font-medium text-black"
                            >
                              Zip
                            </label>
                            <Input
                              type="text"
                              id="buildingZip"
                              autoComplete="off"
                              className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                              {...register("buildingZip")}
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <Button
                            disabled={isSubmitting}
                            className="w-full bg-[#16A34A] hover:bg-[#1bc459]"
                            type="submit"
                            onClick={() => {
                              setIsEditOpen(false);
                            }}
                          >
                            Edit Customer
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                  <Dialog
                    open={isDeleteDialogOpen}
                    onOpenChange={setIsDeleteDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button className="w-full rounded-b-md rounded-t-none bg-[#FAFAFA] text-black hover:bg-gray-200">
                        Delete
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="border-transparent bg-inherit">
                      <DialogHeader>
                        <DialogTitle className="text-black">
                          Are you sure you want to delete?
                        </DialogTitle>
                        <DialogDescription>
                          This action can't be undone
                        </DialogDescription>
                      </DialogHeader>
                      <Button
                        className="bg-red-400 text-white hover:bg-red-500"
                        onClick={async () => {
                          try {
                            await deleteBuildingFn({
                              buildingId: buildings.id,
                            });
                            setIsDeleteDialogOpen(false);
                          } catch (error) {
                            console.error("Error deleting expense:", error);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </DialogContent>
                  </Dialog>
                </PopoverContent>
              </Popover>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default BuildingTable;
